/* import __COLOCATED_TEMPLATE__ from './base-analytics.hbs'; */
import { alias, gt, or, readOnly } from '@ember/object/computed';
import { Promise } from 'rsvp';
import { htmlSafe } from '@ember/template';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import EmberObject, { computed, action } from '@ember/object';
import {
  convertToUTCDateForFormat
} from "../../utils/date-and-time";

export default Component.extend({
  /**
   * Register services
   */
  errorTracker: service('error-tracker'),
  moment: service(),
  userCommandService : service('commands/user-commands'),
  session: service(),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  classNames: ['company-analytics'],
  classNameBindings: [
    '_isSupportsPaging:company-analytics--supports-paging'
  ],

  ///////////
  // PROPS //
  ///////////
  sumParam: undefined,
  groupParam: undefined,
  data: [],
  factoids: [],

  componentAboveGraph: null,

  isLoading: false,

  firstTimeLoading: true,
  loadedYears: {},
  projectCounts: undefined,

  activeFilters: new Map(),
  filterResetValue: 'RESET_FILTER',

  /********************************************************************
   * Properties for paging.  Children should update these as needed.  *
   ********************************************************************/

  /**
   * @type {Number}
   */
  _currentPage: 1,

  /**
   * @type {Pagination}
   */
  _currentPagination: null,

  /**
   * @type {Boolean}      True when we are loading a new page of results in our paging.
   */
  _isLoadingNewPage: false,

  /**
   * @type {Boolean}      Set this to true if your child will support paging.
   */
  _isSupportsPaging: false,

  /**
   * @type {Number}       Number of items we'll pull per page.
   */
  _pageSize: 10,

  /**
   * Set this to true to show the links on the bottom of the chart to allow selecting a group.  Groups refer
   * to how the bars are stacked and grouped together.
   *
   * @see chartGroupParams to see which grouped options are available for this chart.
   *
   * @param {Boolean}
   */
  isShowGroupFilterLinks: true,

  //////////////
  // COMPUTED //
  //////////////

  /**
   * @type {Boolean}      True when we have more pages in our list pagination
   */
  _isInfiniteScrollEnabled: computed(
  '_currentPagination.currentPage',
  '_currentPagination.totalPages',
  function() {
    return this.get('_currentPagination.currentPage') < this.get('_currentPagination.totalPages');
  }),

  shouldShowFullPageLoader: computed('firstTimeLoading', 'noData', function() {
    const loading = this.get('firstTimeLoading');
    const noData = this.get('noData');

    return loading && !noData;
  }),

  shouldShowChartLoader: computed('isLoading', 'noData', function() {
    const loading = this.get('isLoading');
    const noData = this.get('noData');

    return loading && !noData;
  }),

  usesCostCodes: alias('currentUser.company.features.uses_cost_codes'),
  usesGroups: gt('chartGroupParams.length', 1),
  usesSums: gt('chartSumParams.length', 1),

  shouldPreventInteraction: or('isLoading', 'firstTimeLoading', 'noData'),
  shouldFadeFullPage: or('firstTimeLoading', 'noData'),
  shouldFadeData: alias('isLoading'),

  /**
   * Set this to true if you want to fade ONLY the data visuals below the factoids.  You should also set the shouldFadeData
   * to false.
   *
   * @param {Boolean}
   */
  shouldFadeDataVisuals: false,

  availableYears: computed('projectCounts', function () {
    const projectCounts = this.get('projectCounts');
    const earliestYear = Math.min.apply(null, (Object.keys(projectCounts)));
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = earliestYear; year <= currentYear; year++) {
      years.push(year);
    }

    return years;
  }),

  noData: computed('availableYears.length', function() {
    const yearCount = this.get('availableYears.length');

    return yearCount === 0;
  }),

  initialYearIdx: computed('availableYears', 'initialYear', function() {
    const availableYears = this.get('availableYears');
    const initialYear = this.get('initialYear');

    const idx = availableYears.findIndex(year => year === initialYear);
    if (idx === -1) {
      // default to current year if the given one is invalid
      return availableYears.length - 1;
    } else {
      return idx;
    }
  }),

  initialGroupParam: computed('chartGroupParams', function() {
    const groupParams = this.get('chartGroupParams');
    if (!groupParams) {
      return null;
    }
    const initialParam = groupParams.find(param => param.defaultParam);

    return initialParam.value || groupParams[0].value;
  }),

  initialSumParam: computed('chartSumParams', function() {
    const sumParams = this.get('chartSumParams');
    if (!sumParams) {
      return null;
    }
    const initialParam = sumParams.find(param => param.defaultParam);

    return initialParam.value || sumParams[0].value;
  }),

  // stupid hack to force the chart component to be re-inserted each time we change data
  // we need to do this because chart.js is inconsistent in respecting new options when calling its update() method
  // specifically, we need to do this so we can hide/show the legend as appropriate
  chartHack: computed('groupedChartData', function() {
    return [Math.random()];
  }),

  // if the sub-class omits the 'chartData' param we fall back to the raw data
  dataForChart: or('chartData', 'data'),

  groupedChartData: computed('dataForChart', 'chartLabels', 'chartKeyParam', 'groupParam', 'sumParam', function() {
    const labels = this.get('chartLabels');
    const data = this.get('dataForChart');
    const groupParam = this.get('groupParam') || 'NULL';
    const sumParam = this.get('sumParam');

    const keyParam = this.get('chartKeyParam');

    return data.reduce((datasets, item) => {
      const group = item.get(groupParam) || 'None';

      if (!datasets[group]) {
        // we need to initialize everything to null to avoid weird graph problems with sparse datasets
        datasets[group] = {};
        Object.keys(labels).forEach(key => {
          datasets[group][key] = null;
        });
      }

      const value = item.get(keyParam);

      if (!datasets[group][value]) {
        datasets[group][value] = 0;
      }
      datasets[group][value] += (sumParam ? item.get(sumParam) : 1);

      return datasets;
    }, {});
  }),

  computedChartMinHeight: computed('groupedChartData', function() {
    const defaultMinHeight = 300; // 300px;
    const groupedChartData = this.get('groupedChartData');
    const keys = Object.keys(groupedChartData);

    return htmlSafe(`min-height: ${defaultMinHeight + (keys.length * 25)}px`);
  }),

  /**
   * Object indexed by the 'Grouped Option Key Name' => Array of RGB values for the specific color.
   *
   * These indices should map to whatever we're using for the grouping data.
   *
   * @type {Object}
   */
  groupedChartGraphColors: EmberObject.create({}),

  filterArray: computed('activeFilters', function() {
    return Array.from(this.activeFilters).map(activeFilter => {
      const [filterObj, option] = activeFilter[1];
      return filterObj.filter.bind(this, option);
    });
  }),

  ///////////////
  // LIFECYCLE //
  ///////////////
  init() {
    this._super(...arguments);
    // need to do this in case we have computed setters on our data prop in a sub-class
    this.set('data', []);
    this.set('activeFilters', new Map());
    this.set('groupParam', this.get('initialGroupParam'));
    this.set('sumParam', this.get('initialSumParam'));

    const initialYearIdx = this.get('initialYearIdx');
    const availableYears = this.get('availableYears');

    this._queryFactoids().then(factoids => {
      if (!this.isDestroying) {
        this.set('factoids', factoids.toArray());
      }
    }).catch(e => {
      if (e) {
        //only if we have an error.  'e' will be false in the case of fake data which is ok.
        this.get('errorTracker').error('_queryFactoids -- error querying for factoid data ' + JSON.stringify(e), this);
      }
    });
    this.selectYear(availableYears[initialYearIdx]);
  },

  selectYear: action(function(year) {
    // todo fix the peekyears to work with the new multi route transitions
    // if (this.get('loadedYears')[year]) {
    if (false) {
      this._peekYear(year);
      this.set('year', year);
    } else {
      if (!this.firstTimeLoading) {
        this.set('isLoading', true);
      }
      this._queryYear(year).then(() => {
        if (!this.isDestroying) {
          this.set('year', year);
        }

        this.loadedYears[year] = true;
      }).catch(e => {
        if (e) {
          //only if we have an error.  'e' will be false in the case of fake data which is ok.
          this.errorTracker.error('_queryYear -- error querying for analytic data', this, e);
        }
      }).finally(() => {
        if (!this.isDestroying) {
          this.set('isLoading', false);
          this.set('firstTimeLoading', false);
        }
      });
    }
  }),

  disableFilter: action(function(filter) {
    const activeFilters = this.activeFilters;
    activeFilters.delete(filter);
    this.notifyPropertyChange('activeFilters');
  }),

  /////////////
  // ACTIONS //
  /////////////
  actions: {
    selectSumParam(param) {
      this.set('sumParam', param);
    },

    selectGroupParam(param) {
      this.set('groupParam', param);
    },

    enableFilterWithOption(filter, option) {
      const activeFilters = this.activeFilters;
      const filterResetValue = this.filterResetValue;
      if (option === filterResetValue) {
        if (Array.from(activeFilters).length > 0) {
          this.disableFilter(filter);
        }
      } else {
        activeFilters.set(filter.label, [filter, option]);
        this.notifyPropertyChange('activeFilters');
      }
    },
  },

  /////////////////////
  // PRIVATE METHODS //
  /////////////////////
  _queryYear(year) {
    if (!year) {
      return Promise.reject(false);
    }

    return this.queryData(year).then(data => {
      if (this.data && !this.isDestroying) {
        this.set('data', data.toArray());
      }
    }).catch(e => {
      if (e) {
        //only if we have an error.  'e' will be false in the case of fake data which is ok.
        this.errorTracker.error('_queryYear -- error querying for analytic data ' + JSON.stringify(e), this);
      }
    });
  },

  _peekYear(year) {
    this.set('data', this.peekData(year));
  },

  _queryFactoids() {
    const companyId = this.get('currentUser.company.id');
    const factoidType = this.get('factoidType');
    return this.get('store').query('factoid', {
      type: factoidType,
      filter: {
        company_id: companyId
      }
    }).catch(e => {
      if (e) {
        //only if we have an error.  'e' will be false in the case of fake data which is ok.
        this.get('errorTracker').error('_queryFactoids -- error querying for factoid data ' + JSON.stringify(e), this);
      }
    });
  },

  ////////////////////
  // HELPER METHODS //
  ////////////////////
  formatMoney(total) {
    return (total || total === 0) ?
      total.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
      }) :
      '-';
  },

  formatMoneyWithoutChange(total) {
    if (!total && total !== 0) {
      return '-';
    }

    const formatted = this.formatMoney(Math.round(total));
    // substring to remove the change from the dollar amount
    return formatted.substring(0, formatted.length - 3);
  },

  formatDate(date) {
    if (!date) {
      return '';
    }

    let utcMoment = convertToUTCDateForFormat(date);
    return utcMoment.format('M/D/YYYY');
  },

  formatArray(items) {
    return items ? htmlSafe(items.join('<br>')) : '-';
  }
});
